import { Icon } from '@frontend/icons';
import type { CommonHTMLLIAttributes } from '@frontend/types';
import { theme } from '@frontend/theme';
import { Button, ContentLoader, Modal, Text, useModalControl } from '@frontend/design-system';
import { LocalMediaState } from '../types';

type MediaPreviewListItemProps = LocalMediaState & {
  onRemove: () => void;
} & CommonHTMLLIAttributes;

const PREVIEW_TILE_SIZE = 80;
const PDF_TILE_WIDTH = 240;

const formatFileSize = (bytes?: number) => {
  if (bytes === undefined || bytes === null || !Number.isFinite(bytes) || bytes < 0) return undefined;
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exp;
  const rounded = value >= 10 || exp === 0 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${units[exp]}`;
};

const detectVariant = ({
  contentType,
  filename,
}: {
  contentType?: string;
  filename?: string;
}): 'image' | 'video' | 'pdf' => {
  if (contentType?.startsWith('video/')) return 'video';
  if (contentType === 'application/pdf') return 'pdf';
  if (contentType?.startsWith('image/')) return 'image';
  const lower = filename?.toLowerCase() ?? '';
  if (lower.endsWith('.mp4') || lower.endsWith('.mov')) return 'video';
  if (lower.endsWith('.pdf')) return 'pdf';
  return 'image';
};

export const MediaPreviewListItem = ({
  localId: _,
  mediaId: __,
  localSrc,
  uploadedSrc,
  contentType,
  filename,
  fileSizeBytes,
  isUploading,
  isDownloading,
  hasError,
  onRemove,
  ...rest
}: MediaPreviewListItemProps) => {
  const { modalProps, triggerProps } = useModalControl();
  const showLoader = isUploading || isDownloading;
  const src = localSrc || uploadedSrc;
  const isInteractable = !showLoader || !!rest.onClick;
  const variant = detectVariant({ contentType, filename });
  const formattedSize = formatFileSize(fileSizeBytes);
  const displayName = filename || (variant === 'pdf' ? 'document.pdf' : variant === 'video' ? 'video' : 'image');

  const tileWidth = variant === 'pdf' ? PDF_TILE_WIDTH : PREVIEW_TILE_SIZE;

  const removeButton = isInteractable ? (
    <Button
      iconName='x-small'
      size='small'
      variant='primary'
      css={{
        position: 'absolute',
        top: theme.spacing(0.5),
        right: theme.spacing(0.5),
        padding: theme.spacing(0.5),
        height: 'auto',
        backgroundColor: theme.colors.white,
        zIndex: 1,
        svg: {
          transition: 'color 200ms ease-in-out',
          color: theme.colors.neutral70,
        },
        ':hover': {
          backgroundColor: theme.colors.critical50,
          svg: {
            color: theme.colors.white,
          },
        },
      }}
      onClick={(e) => {
        e.stopPropagation();
        onRemove();
      }}
    />
  ) : null;

  return (
    <>
      <li
        css={{
          position: 'relative',
          borderRadius: theme.borderRadius.small,
          overflow: 'hidden',
          flex: '0 0 auto',
          width: tileWidth,
          height: PREVIEW_TILE_SIZE,
          backgroundColor: hasError ? theme.colors.critical5 : theme.colors.neutral5,
        }}
        {...rest}
      >
        {removeButton}
        <ContentLoader show={showLoader} />

        {variant === 'image' && src && (
          <img
            src={src}
            alt={displayName}
            css={{
              height: '100%',
              width: '100%',
              objectFit: 'cover',
              cursor: isInteractable ? 'pointer' : 'default',
              display: 'block',
            }}
            {...triggerProps}
          />
        )}

        {variant === 'video' && src && (
          <div
            css={{
              position: 'relative',
              width: '100%',
              height: '100%',
              cursor: isInteractable ? 'pointer' : 'default',
              backgroundColor: theme.colors.neutral90,
            }}
            {...triggerProps}
          >
            <video
              src={src}
              preload='metadata'
              muted
              playsInline
              css={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                pointerEvents: 'none',
              }}
            />
            <div
              aria-hidden='true'
              css={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.colors.white,
              }}
            >
              <div
                css={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0, 0, 0, 0.55)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon name='play' size={16} />
              </div>
            </div>
          </div>
        )}

        {variant === 'pdf' && (
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(1.5),
              padding: theme.spacing(1.5),
              width: '100%',
              height: '100%',
              cursor: isInteractable ? 'pointer' : 'default',
            }}
            {...(src ? triggerProps : {})}
          >
            <div
              aria-hidden='true'
              css={{
                flex: '0 0 auto',
                position: 'relative',
                width: 32,
                height: 40,
                color: theme.colors.white,
                filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.15)) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.08))',
              }}
            >
              <svg
                viewBox='0 0 32 40'
                width={32}
                height={40}
                preserveAspectRatio='none'
                css={{ position: 'absolute', inset: 0, display: 'block' }}
              >
                <path d='M5 0 H22 L32 10 V35 Q32 40 27 40 H5 Q0 40 0 35 V5 Q0 0 5 0 Z' fill='currentColor' />
              </svg>
              <span
                css={{
                  position: 'absolute',
                  top: '60%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontSize: 10,
                  fontWeight: 700,
                  lineHeight: 1,
                  letterSpacing: '0.5px',
                  color: theme.colors.primary50,
                  pointerEvents: 'none',
                }}
              >
                PDF
              </span>
            </div>
            <div css={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: theme.spacing(0.25) }}>
              <Text
                size='medium'
                weight='bold'
                color='primary'
                css={{
                  margin: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayName}
              </Text>
              {formattedSize && (
                <Text size='small' color='subdued' css={{ margin: 0 }}>
                  {formattedSize}
                </Text>
              )}
            </div>
          </div>
        )}
      </li>
      {variant === 'image' && (
        <Modal
          {...modalProps}
          maxWidth={0}
          css={{
            padding: 0,
            borderRadius: theme.borderRadius.medium,
            overflow: 'hidden',
          }}
        >
          <img src={src} css={{ maxHeight: '70vh', maxWidth: '50vw', padding: 0 }} />
        </Modal>
      )}
    </>
  );
};
